import { Component, OnInit } from '@angular/core';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LanguageOption, LanguageService } from '@service/language.service';
import { NotificationService } from '@service/notification.service';
import { AzureStorageService } from '@service/project-settings/azure-storage.service';
import { VesselBuilderSelectService } from '@service/project-settings/vessel-builder-select.service';
import { VesselSelectService } from '@service/project-settings/vessel-select.service';
import { ValidationError } from 'ops/common/validators';
import { LoadingSpinnerComponent } from '@components/loading-spinner/loading-spinner.component';
import { AuthService } from '@service/auth.service';
import { VesselService } from '@service/vessel.service';
import { Vessel } from 'ops/common/models/vessel';

enum EditType {
    VESSEL_NAME = 'vesselName',
    VESSEL_NO = 'vesselNumber',
}

@Component({
    selector: 'app-project-settings-form',
    templateUrl: './project-settings-form.component.html',
    styleUrls: ['./project-settings-form.component.scss'],
    standalone: false
})
export class ProjectSettingsFormComponent implements OnInit {
    languageType: LanguageOption[];

    vessel: Vessel;
    settings: any;

    isError: boolean = false;
    regexError = {
        [EditType.VESSEL_NO]: '',
        [EditType.VESSEL_NAME]: '',
    };
    validationErrors: { [propertyName: string]: ValidationError } = {};

    spinnerOverlayRef = this.overlay.create({
        hasBackdrop: true,
        positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
    });

    constructor(
        private vesselSelectService: VesselSelectService,
        private vesselBuilderSelectService: VesselBuilderSelectService,
        private azureStorageService: AzureStorageService,
        public dialogRef: MatDialogRef<any>,
        public dialog: MatDialog,
        private notificationService: NotificationService,
        private languageService: LanguageService,
        private overlay: Overlay,
        private authService: AuthService,
        private vesselService: VesselService,
    ) {
        this.languageType = this.languageService.getLanguages();
    }

    async ngOnInit() {
        const vessel = await this.vesselService.loadVessel();
        if (vessel) {
            this.vessel = vessel;
        } else {
            this.vessel = {
                name: undefined,
                numberOps: 0,
                numberFcu: 0,
                projectVersion: undefined,
                iAPIVesselId: undefined,
                shipyardName: undefined,
                iAPIShipyardId: undefined,
            };
        }
    }

    /**
     * Open shipyard and vessel select dialog
     */
    async onSelectBuilder() {
        try {
            const builder = await this.vesselBuilderSelectService.openBuilderSelectDialog();
            if (builder) {
                this.vessel.shipyardName = builder.name;
                this.vessel.iAPIShipyardId = builder.id;
                this.showVesselList(builder.id, builder.name);
            }
        } catch (err) {
            console.log(err);
        }
    }

    async showVesselList(builderId: string, builderName: string) {
        const vesselData = await this.vesselSelectService.openVesselSelectDialog(
            builderId,
            builderName,
        );
        if (vesselData) {
            this.vessel.iAPIVesselId = vesselData.id;
            this.vessel.name = vesselData.name;
        } else {
            console.log('no vessel data found');
        }
    }

    async projectVersionList() {
        if (this.vessel.iAPIShipyardId && this.vessel.iAPIVesselId) {
            const blobVersion = await this.azureStorageService.openVersionList(this.vessel);
            if (blobVersion) {
                this.vessel.projectVersion = blobVersion;
            }
        }
    }

    hasProjectData() {
        if (!this.vessel) {
            return false;
        }
        return this.vessel.iAPIShipyardId && this.vessel.iAPIVesselId;
    }

    saveIsEnabled() {
        if (!this.vessel) {
            return false;
        }
        return this.vessel.iAPIShipyardId && this.vessel.iAPIVesselId && this.vessel.projectVersion;
    }

    onNoClick() {
        this.dialogRef.close();
    }

    async handleSave() {
        try {
            const builderId = this.vessel.iAPIShipyardId;
            const vesselId = this.vessel.iAPIVesselId;
            const blobVersion = this.vessel.projectVersion;
            const blobData = {
                builderId: builderId,
                vesselId: vesselId,
                blobVersion: blobVersion,
            };
            if (!this.spinnerOverlayRef.hasAttached()) {
                this.spinnerOverlayRef.attach(new ComponentPortal(LoadingSpinnerComponent));
            }
            const pulledProject = await this.azureStorageService.getProjectFromBlob(blobData);

            if (pulledProject) {
                pulledProject.blobVersion = blobVersion;
                await this.vesselService.setBlobContentInDb(pulledProject);
                this.authService.updateUserList();
            }
            this.notificationService.projectSaveCompleted();
            this.spinnerOverlayRef.detach();
        } catch (err) {
            this.spinnerOverlayRef.detach();
            this.notificationService.projectSaveError();
        }
    }

    onLanguageChange(language) {
        this.settings.language = language;
    }
}
